// Customer-visible name: "H3ightboard". Internal symbols (file path, route,
// hook names, component name) intentionally remain "staffing-board" / StaffingBoard.
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useOrgCurrency, useOrgTimeZone, useOrgToday } from '@/hooks/use-settings';
import { formatDateOnly } from '@/lib/date-utils';
import { toast } from 'sonner';
import { DndProvider, Preview } from 'react-dnd-multi-backend';
import { HTML5toTouch } from 'rdndmb-html5-to-touch';
import { ProjectColumn } from './project-column';
import { ConfirmAssignDialog } from './confirm-assign-dialog';
import { removeConfirmMessage } from './remove-confirm-message';
import { DispatchDialog } from './dispatch-dialog';
import { DispatchHistoryDrawer } from './dispatch-history-drawer';
import { WorkPackagePanel } from './work-package-panel';
import { FindCrewDialog } from './find-crew-dialog';
import { ComplianceEvidencePanel } from './compliance-evidence-panel';
import { SupervisionCockpitPanel } from './supervision-cockpit-panel';
import { TimelineView } from './timeline-view';
import { TodayAtAGlanceBanner } from './today-at-a-glance-banner';
import type { GlanceFilter } from './today-at-a-glance-banner';
import { getTimeRangeWindow, isProjectActiveInWindow } from './board-time-range';
import type { BoardTimeRange } from './board-time-range';
import {
  getCardReadinessReasons,
  hasGearFailed,
  hasGearInspectionDue,
  hasGearLifeExceeded,
} from './card-readiness';
import { getJobColor } from './job-colors';
import { H3ightboardUnsavedChangesDialog } from './unsaved-changes-dialog';
import { useH3ightboardUnsavedChangesGuard } from './use-unsaved-changes-guard';
import {
  useStaffingBoard,
  useAssignTech,
  useUnassignTech,
  useMoveTech,
  useBulkAssignTechs,
  useRestoreAssignment,
  useCrewLists,
  useSupervisionCockpit,
  useBoardRollup,
  isConfirmRequired,
  isConfirmationExpired,
  DEFAULT_TERMINOLOGY,
} from '@/hooks/use-staffing-board';
import type {
  BoardTech,
  BoardAssignment,
  AssignWarning,
  BulkAssignPayload,
  BulkConfirmRequiredResponse,
  ConfirmRequiredResponse,
  BoardTerminology,
} from '@/hooks/use-staffing-board';
import { Search, Filter, Loader2, Printer, FileDown, LayoutGrid, CalendarDays, ClipboardCheck, History, ShieldAlert, Eye } from 'lucide-react';
import { usePermissions } from '@/hooks/use-permissions';
import { useBoardPersonaDefaults } from './use-board-persona-defaults';
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts';
import { ShortcutOverlay } from './shortcut-overlay';
import { StickyNotesPanel } from './sticky-notes-panel';
import { DemoChecklistBanner } from '@/components/demo/demo-checklist-banner';
import { useIsTouchDevice } from '@/hooks/use-touch-device';
import { TouchAssignPicker } from './touch-assign-picker';
import { ExecutiveRollupBanner } from './executive-rollup-banner';
import { useMemberships } from '@/hooks/use-memberships';
import { useAuth } from '@/contexts/auth-context';
import { useBoardPresence } from '@/hooks/use-board-presence';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/app/components/ui/tooltip';

type BoardView = 'board' | 'timeline';

const COST_REVEAL_STORAGE_PREFIX = 'h3_staffing_board_costs_revealed';

function getCostRevealStorageKey(userId: number | null | undefined, organizationId: number | null | undefined) {
  if (userId == null || organizationId == null) return null;
  return `${COST_REVEAL_STORAGE_PREFIX}:${userId}:${organizationId}`;
}

function readCostRevealState(storageKey: string | null) {
  if (storageKey == null || typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
}

function writeCostRevealState(storageKey: string | null, next: boolean) {
  if (storageKey == null || typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(storageKey, next ? 'true' : 'false');
  } catch {
    // Ignore storage failures.
  }
}

function resolveTechDisplayName(
  techs: BoardTech[] | undefined,
  techId?: number,
  personId?: number,
): string | undefined {
  const tech = techs?.find((candidate) =>
    (techId != null && candidate.id === techId)
    || (personId != null && candidate.person_id === personId),
  );
  if (tech) return `${tech.first_name} ${tech.last_name}`;
  if (techId != null) return `Tech #${techId}`;
  if (personId != null) return `Person #${personId}`;
  return undefined;
}

interface DragItem {
  techId: number;
  personId: number;
  assignmentId?: number;
  sourceProjectId?: number;
  sourceJobId?: number;
}

interface BulkMoveItem {
  techId: number;
  personId: number;
  name: string;
  previousProjectId: number;
  previousJobId: number;
  targetProjectId: number;
  targetJobId: number;
  assignment?: BoardAssignment;
  isFoundation: boolean;
}

interface BulkRemoveItem {
  techId: number;
  personId: number;
  name: string;
  previousProjectId: number;
  previousJobId: number;
  assignment?: BoardAssignment;
  isFoundation: boolean;
}

interface PendingConfirm {
  id: number;
  action: 'assign' | 'move' | 'double-book' | 'unassign' | 'bulk-assign' | 'bulk-move' | 'bulk-remove';
  warnings?: AssignWarning[];
  confirmationToken?: string;
  /** False when the server says this user may not override certification warnings. */
  canOverride?: boolean;
  message?: string;
  confirmLabel?: string;
  pendingLabel?: string;
  title?: string;
  names?: string[];
  affectedNames?: string[];
  techName?: string;
  projectName?: string;
  assignPayload?: { tech_id?: number; person_id?: number; project_id?: number; job_id?: number; role?: string };
  bulkAssignPayload?: BulkAssignPayload;
  movePayload?: { assignment_id?: number; project_id?: number; person_id?: number; job_id?: number; from_job_id?: number };
  unassignPayload?: { assignment_id?: number; person_id: number; job_id?: number };
  previousAssignment?: BoardAssignment;
  previousJobId?: number;
  bulkMoveItems?: BulkMoveItem[];
  bulkRemoveItems?: BulkRemoveItem[];
}

interface StaffingHistoryTarget {
  id: string;
  message: string;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
}

interface BoardMoveSnapshot {
  previousAssignment: BoardAssignment;
  projectId: number;
  newAssignmentId?: number;
}

interface FoundationMoveInfo {
  person_id: number;
  previousJobId: number;
  targetJobId: number;
}

interface FoundationAssignInfo {
  person_id: number;
  job_id: number;
  /** Crew assignment this assign created, removed again on undo. */
  created_dispatch_id?: number;
}

const STAFFING_HISTORY_TOAST_DURATION_MS = 10000;
const STAFFING_HISTORY_LIMIT = 25;

function TouchDragPreview() {
  return (
    <Preview<DragItem>
      generator={({ item, style }) => (
        <div
          style={{
            ...style,
            width: 256,
            opacity: 0.85,
            pointerEvents: 'none',
            zIndex: 9999,
          }}
          className="rounded-lg border-l-4 border-l-blue-600 bg-blue-50 shadow-lg px-3 py-2"
        >
          <p className="text-sm font-semibold text-gray-900 truncate">
            Tech #{item.techId}
          </p>
          <p className="text-xs text-gray-500">Dragging…</p>
        </div>
      )}
    />
  );
}

/** Summary rows (Organization Rollup cards + Today at-a-glance chips) above the board. Set to true to restore them. */
const SHOW_BOARD_SUMMARY_ROWS = false;

export function StaffingBoardPage() {
  const orgToday = useOrgToday();
  const timeZone = useOrgTimeZone();
  const currency = useOrgCurrency();
  const [activeView, setActiveView] = useState<BoardView>('board');
  const { data, isLoading, error } = useStaffingBoard();
  const { boardPersona, hasMinRole, canViewFinancials, hasModuleAccess } = usePermissions();
  const { user } = useAuth();
  const { data: presenceViewers = [] } = useBoardPresence();
  const terminology: BoardTerminology = data?.terminology ?? DEFAULT_TERMINOLOGY;
  const { defaults: personaDefaults, markManualOverride } = useBoardPersonaDefaults(boardPersona, terminology);

  const [pendingConfirms, setPendingConfirms] = useState<PendingConfirm[]>([]);
  const pendingConfirm: PendingConfirm | null = pendingConfirms[0] ?? null;
  const pendingConfirmIdRef = useRef(0);
  const enqueuePendingConfirm = useCallback((
    confirm: Omit<PendingConfirm, 'id'>,
    supersedesToken?: string,
  ) => {
    const id = pendingConfirmIdRef.current++;
    setPendingConfirms((prev) => {
      if (supersedesToken != null) {
        const replacementIndex = prev.findIndex((entry) => entry.confirmationToken === supersedesToken);
        if (replacementIndex >= 0) {
          const next = [...prev];
          next[replacementIndex] = { ...confirm, id };
          return next;
        }
      }
      return [...prev, { ...confirm, id }];
    });
  }, []);
  const dequeuePendingConfirm = useCallback((id: number) => {
    setPendingConfirms((prev) => prev.filter((entry) => entry.id !== id));
  }, []);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [evidencePanelOpen, setEvidencePanelOpen] = useState(
    () => personaDefaults.evidencePanelOpen,
  );
  const [shortcutOverlayOpen, setShortcutOverlayOpen] = useState(false);
  const [stickyNotesPanelOpen, setStickyNotesPanelOpen] = useState(false);
  const [workPackageProjectId, setWorkPackageProjectId] = useState<number | null>(null);
  const [findCrewProjectId, setFindCrewProjectId] = useState<number | null>(null);
  const [supervisionPanelOpen, setSupervisionPanelOpen] = useState(false);
  const [touchAssignTarget, setTouchAssignTarget] = useState<{ techId: number; personId: number; techName: string; assignmentId?: number } | null>(null);

  const isOwnerPersona = boardPersona === 'owner';
  const { data: rollupData } = useBoardRollup(SHOW_BOARD_SUMMARY_ROWS && isOwnerPersona);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isTouchDevice = useIsTouchDevice();

  const assignMutation = useAssignTech(
    (resp: ConfirmRequiredResponse, originalPayload) => {
      enqueuePendingConfirm({
        action: 'assign',
        warnings: resp.warnings,
        confirmationToken: resp.confirmation_token,
        canOverride: resp.can_override,
        affectedNames: [resolveTechDisplayName(
          data?.techs,
          originalPayload.tech_id,
          originalPayload.person_id,
        )].filter((name): name is string => name != null),
        assignPayload: originalPayload,
      }, originalPayload.confirmed === true ? originalPayload.confirmation_token : undefined);
    },
  );
  const unassignMutation = useUnassignTech();
  const restoreAssignmentMutation = useRestoreAssignment();
  const moveMutation = useMoveTech(
    (resp: ConfirmRequiredResponse, originalPayload) => {
      const prevJobId = originalPayload.from_job_id
        ?? (originalPayload.person_id != null && !originalPayload.assignment_id
          ? data?.board_placements.find((bp) => bp.person_id === originalPayload.person_id)?.job_id
          : undefined);
      const previousAssignment = (() => {
        const assignmentId = originalPayload.assignment_id;
        const personId = originalPayload.person_id;
        const fromJobId = originalPayload.from_job_id;
        if (assignmentId != null) {
          return data?.assignments.find((a) => a.id === assignmentId || a.assignment_id === assignmentId);
        }
        if (personId != null && fromJobId != null) {
          const bp = data?.board_placements.find((b) => b.person_id === personId && b.job_id === fromJobId);
          if (bp != null) {
            return data?.assignments.find((a) => a.id === bp.id);
          }
        }
        return data?.assignments.find((a) => a.project_id === originalPayload.project_id && a.tech_id === personId);
      })();
      enqueuePendingConfirm({
        action: 'move',
        warnings: resp.warnings,
        confirmationToken: resp.confirmation_token,
        canOverride: resp.can_override,
        affectedNames: [resolveTechDisplayName(
          data?.techs,
          undefined,
          originalPayload.person_id,
        )].filter((name): name is string => name != null),
        confirmLabel: 'Move',
        pendingLabel: 'Moving...',
        movePayload: originalPayload,
        previousAssignment,
        previousJobId: prevJobId,
      }, originalPayload.confirmed === true ? originalPayload.confirmation_token : undefined);
    },
  );
  const undoMoveMutation = useMoveTech();
  const undoAssignMutation = useAssignTech();
  const bulkAssignMutation = useBulkAssignTechs(
    (resp: BulkConfirmRequiredResponse, originalPayload: BulkAssignPayload) => {
      const warningsByTech = resp.warnings_by_tech ?? {};
      const names = Object.entries(warningsByTech).map(([techId, warnings]) => {
        const tech = data?.techs.find((candidate) => candidate.id === Number(techId));
        const name = tech ? `${tech.first_name} ${tech.last_name}` : `Tech #${techId}`;
        return `${name}: ${warnings.map((warning) => warning.message).join('; ')}`;
      });
      const affectedNames = Array.from(new Set(Object.keys(warningsByTech).map((techId) =>
        resolveTechDisplayName(data?.techs, Number(techId)),
      ).filter((name): name is string => name != null)));
      const uniqueWarnings = Array.from(
        new Map(
          resp.warnings.map((warning) => {
            const warningWithoutTech = { ...warning };
            delete warningWithoutTech.tech_id;
            return [
              warning.message,
              warningWithoutTech,
            ];
          }),
        ).values(),
      );
      enqueuePendingConfirm({
        action: 'bulk-assign',
        warnings: uniqueWarnings,
        names,
        affectedNames,
        confirmationToken: resp.confirmation_token,
        canOverride: resp.can_override,
        bulkAssignPayload: originalPayload,
        title: 'Assign crew?',
        message: 'The following issues were detected across this crew batch. Do you want to proceed?',
        confirmLabel: 'Assign Anyway',
        pendingLabel: 'Assigning...',
      }, originalPayload.confirmed === true ? originalPayload.confirmation_token : undefined);
    },
  );

  const [selectedTechIds, setSelectedTechIds] = useState<Set<number>>(new Set());

  // F04: track tech IDs that were just dropped (for settle animation)
  const [droppedTechIds, setDroppedTechIds] = useState<Set<number>>(new Set());
  const boardHasMounted = useRef(false);
  useEffect(() => { boardHasMounted.current = true; }, []);

  const crewEnabled = data?.modules.crew ?? false;
  // crew_lists/ is refused without Crew access; don't ask for it.
  const { data: crewLists } = useCrewLists(crewEnabled && hasModuleAccess('crew'));

  const { data: memberships } = useMemberships();
  const currentOrg = memberships?.find((m) => m.is_current);
  const orgName = currentOrg?.organization_name ?? '';
  const costRevealStorageKey = useMemo(
    () => getCostRevealStorageKey(user?.id, user?.organization),
    [user?.id, user?.organization],
  );
  const [costsRevealed, setCostsRevealed] = useState(false);

  const [search, setSearch] = useState('');
  const [certFilter, setCertFilter] = useState<string>('');
  const [skillFilter, setSkillFilter] = useState('');
  const [projectStatusFilter, setProjectStatusFilter] = useState<string>(
    () => personaDefaults.projectStatusFilter,
  );
  const [showFilters, setShowFilters] = useState(
    () => personaDefaults.showFilters,
  );
  const [glanceFilter, setGlanceFilter] = useState<GlanceFilter>(
    () => personaDefaults.glanceFilter,
  );
  const [timeRange, setTimeRange] = useState<BoardTimeRange>('all');

  const projectsEnabled = data?.modules.projects ?? false;
  const safetyEnabled = data?.modules.safety ?? false;
  const inventoryEnabled = data?.modules.inventory ?? false;
  const payrollEnabled = data?.modules.payroll ?? false;
  const complianceEnabled = data?.modules.compliance ?? false;
  const showFinancials = payrollEnabled && canViewFinancials;
  const canEdit = data?.can_edit ?? false;
  const canDispatch = data?.can_dispatch ?? false;
  const undispatchedAssignments = useMemo(
    () => (data?.assignments ?? []).filter(
      (assignment) => assignment.status === 'scheduled' && assignment.validated_via_board,
    ),
    [data?.assignments],
  );
  const undispatchedAssignmentIds = useMemo(
    () => undispatchedAssignments
      .map((assignment) => assignment.assignment_id)
      .filter((assignmentId): assignmentId is number => assignmentId !== undefined)
      .sort((a, b) => a - b),
    [undispatchedAssignments],
  );
  const hasUndispatchedPlacements = undispatchedAssignments.length > 0;
  const dispatchReadinessReasons = useMemo(() => {
    if (!data || undispatchedAssignments.length === 0) return [];

    const projectIds = new Set(undispatchedAssignments.map((assignment) => assignment.project_id));
    const techsById = new Map(data.techs.map((tech) => [tech.id, tech]));
    const projectTechs = new Map<number, BoardTech[]>();
    for (const assignment of undispatchedAssignments) {
      const tech = techsById.get(assignment.tech_id);
      if (!tech) continue;
      const techs = projectTechs.get(assignment.project_id) ?? [];
      if (!techs.some((candidate) => candidate.id === tech.id)) techs.push(tech);
      projectTechs.set(assignment.project_id, techs);
    }

    return data.projects
      .filter((project) => projectIds.has(project.id))
      .flatMap((project) => getCardReadinessReasons(
        project,
        projectTechs.get(project.id) ?? [],
        {
          safetyEnabled,
          inventoryEnabled,
          crewEnabled,
          includeDispatchedProjectReasons: true,
        },
      ));
  }, [
    data,
    undispatchedAssignments,
    safetyEnabled,
    inventoryEnabled,
    crewEnabled,
  ]);
  const dispatchButtonState = !hasUndispatchedPlacements
    ? 'empty'
    : dispatchReadinessReasons.some((reason) => reason.severity === 'blocked')
      ? 'blocked'
      : dispatchReadinessReasons.length > 0
        ? 'warning'
        : 'ready';
  const dispatchButtonClass = {
    empty: 'bg-gray-200 text-gray-500 cursor-not-allowed',
    ready: 'bg-green-600 text-white hover:bg-green-700',
    warning: 'bg-yellow-400 text-yellow-950 hover:bg-yellow-500',
    blocked: 'bg-red-600 text-white hover:bg-red-700',
  }[dispatchButtonState];
  const restrictionReason = !canEdit
    ? (personaDefaults.isReadOnly ? personaDefaults.readOnlyReason : personaDefaults.limitedAccessReason)
    : undefined;

  useEffect(() => {
    setCostsRevealed(readCostRevealState(costRevealStorageKey));
  }, [costRevealStorageKey]);

  const handleToggleCosts = useCallback(() => {
    setCostsRevealed((previous) => {
      const next = !previous;
      writeCostRevealState(costRevealStorageKey, next);
      return next;
    });
  }, [costRevealStorageKey]);

  const canViewSupervision = hasMinRole('Supervisor') && boardPersona !== 'viewer' && boardPersona !== 'crew_tech' && (crewEnabled || complianceEnabled);
  const { data: supervisionData } = useSupervisionCockpit(canViewSupervision);
  const supervisionGapProjectIds = useMemo(() => {
    if (!supervisionData) return new Set<number>();
    return new Set(supervisionData.queues.missing_l3.map((item) => item.project_id));
  }, [supervisionData]);

  // Clear glance filter if it targets a disabled module (e.g. compliance
  // persona defaults to 'missing-jsa' but safety may be off on free tier).
  useEffect(() => {
    if (!data) return; // Don't clear during loading — modules not yet known
    if (glanceFilter === 'missing-jsa' && !safetyEnabled) setGlanceFilter(null);
    if (glanceFilter === 'expired-certs' && !crewEnabled) setGlanceFilter(null);
    if (glanceFilter === 'gear-overdue' && !inventoryEnabled) setGlanceFilter(null);
    if (glanceFilter === 'gear-failed' && !inventoryEnabled) setGlanceFilter(null);
    if (glanceFilter === 'gear-life-exceeded' && !inventoryEnabled) setGlanceFilter(null);
    if (glanceFilter === 'payroll-alerts' && (!payrollEnabled || !data?.payroll_summary)) setGlanceFilter(null);
  }, [data, glanceFilter, safetyEnabled, crewEnabled, inventoryEnabled, payrollEnabled]);

  const staffingUndoStackRef = useRef<StaffingHistoryTarget[]>([]);
  const staffingRedoStackRef = useRef<StaffingHistoryTarget[]>([]);
  const staffingUndoToastIdRef = useRef<string | number | null>(null);

  const runStaffingHistory = useCallback((direction: 'undo' | 'redo') => {
    const source = direction === 'undo' ? staffingUndoStackRef.current : staffingRedoStackRef.current;
    const target = source.pop();
    if (!target) {
      toast.error(direction === 'undo' ? 'Nothing to undo.' : 'Nothing to redo.');
      return;
    }

    const destination = direction === 'undo' ? staffingRedoStackRef.current : staffingUndoStackRef.current;
    const promise = target[direction]().then(() => {
      destination.push(target);
      if (destination.length > STAFFING_HISTORY_LIMIT) {
        destination.shift();
      }
    });

    toast.promise(promise, {
      loading: direction === 'undo' ? 'Undoing H3ightboard change…' : 'Redoing H3ightboard change…',
      success: direction === 'undo' ? 'H3ightboard change undone' : 'H3ightboard change redone',
      error: (error) => error instanceof Error && error.message
        ? error.message
        : direction === 'undo'
          ? 'Failed to undo H3ightboard change'
          : 'Failed to redo H3ightboard change',
    });
  }, []);

  const pushStaffingHistory = useCallback((target: Omit<StaffingHistoryTarget, 'id'>) => {
    const historyTarget: StaffingHistoryTarget = { ...target, id: `${Date.now()}-${Math.random()}` };
    staffingUndoStackRef.current.push(historyTarget);
    if (staffingUndoStackRef.current.length > STAFFING_HISTORY_LIMIT) {
      staffingUndoStackRef.current.shift();
    }
    staffingRedoStackRef.current = [];

    if (staffingUndoToastIdRef.current !== null) {
      toast.dismiss(staffingUndoToastIdRef.current);
    }

    const toastId = toast.success(target.message, {
      description: `${staffingUndoStackRef.current.length} undoable board action${staffingUndoStackRef.current.length === 1 ? '' : 's'}. Press Ctrl+Z to step back.`,
      duration: STAFFING_HISTORY_TOAST_DURATION_MS,
      action: {
        label: 'Undo',
        onClick: () => runStaffingHistory('undo'),
      },
    });
    staffingUndoToastIdRef.current = toastId;
  }, [runStaffingHistory]);

  const restoreAssignment = useCallback((assignment: BoardAssignment) => restoreAssignmentMutation.mutateAsync({
    assignment_id: assignment.id,
    project_id: assignment.project_id,
    // The exact crew assignment the person had; null means they had none.
    dispatch_id: assignment.assignment_id ?? null,
    role: assignment.role,
    start_date: assignment.start_date,
    end_date: assignment.end_date,
  }), [restoreAssignmentMutation]);

  // Undo of a removal made by person + job (no board assignment snapshot):
  // put the same placement back without re-running the assign checks.
  const restoreFoundationPlacement = useCallback((personId: number, jobId: number) => restoreAssignmentMutation.mutateAsync({
    person_id: personId,
    project_id: jobId,
    dispatch_id: null,
  }), [restoreAssignmentMutation]);

  // Undo of an assign: take the card off the board and, when that assign
  // created the crew assignment, take it off the crew schedule too.
  const undoAssign = useCallback((assignment: BoardAssignment) => unassignMutation.mutateAsync({
    assignment_id: assignment.id,
    ...(assignment.dispatch_created && assignment.assignment_id != null
      ? { undo_assign_dispatch_id: assignment.assignment_id }
      : {}),
  }), [unassignMutation]);

  const moveAssignment = useCallback(async (assignmentId: number, projectId: number, failureMessage: string) => {
    const resp = await undoMoveMutation.mutateAsync({
      assignment_id: assignmentId,
      project_id: projectId,
    });
    if (isConfirmRequired(resp)) {
      throw new Error(failureMessage);
    }
  }, [undoMoveMutation]);

  const registerAssignUndo = useCallback((assignment: BoardAssignment) => {
    pushStaffingHistory({
      message: 'H3ightboard assignment saved',
      undo: async () => {
        await undoAssign(assignment);
      },
      redo: async () => {
        await restoreAssignment(assignment);
      },
    });
  }, [pushStaffingHistory, restoreAssignment, undoAssign]);

  const registerMoveUndo = useCallback((previousAssignment: BoardAssignment, projectId: number, newAssignmentId?: number) => {
    const sourceAssignmentId = previousAssignment.id;
    pushStaffingHistory({
      message: 'H3ightboard move saved',
      undo: () => moveAssignment(
        newAssignmentId ?? sourceAssignmentId,
        previousAssignment.project_id,
        'Undo could not be applied because it now requires confirmation.',
      ),
      redo: () => moveAssignment(
        sourceAssignmentId,
        projectId,
        'Redo could not be applied because it now requires confirmation.',
      ),
    });
  }, [moveAssignment, pushStaffingHistory]);

  const registerUnassignUndo = useCallback((assignment: BoardAssignment) => {
    pushStaffingHistory({
      message: 'H3ightboard assignment removed',
      undo: async () => {
        await restoreAssignment(assignment);
      },
      redo: async () => {
        await unassignMutation.mutateAsync({ assignment_id: assignment.id });
      },
    });
  }, [pushStaffingHistory, restoreAssignment, unassignMutation]);

  const foundationAssign = useCallback(async (personId: number, jobId: number, failureMessage: string) => {
    const resp = await undoAssignMutation.mutateAsync({ person_id: personId, job_id: jobId });
    if (isConfirmRequired(resp)) {
      throw new Error(failureMessage);
    }
  }, [undoAssignMutation]);

  const foundationMove = useCallback(async (personId: number, jobId: number, failureMessage: string, fromJobId?: number) => {
    const resp = await undoMoveMutation.mutateAsync({ person_id: personId, job_id: jobId, from_job_id: fromJobId });
    if (isConfirmRequired(resp)) {
      throw new Error(failureMessage);
    }
  }, [undoMoveMutation]);

  const registerFoundationAssignUndo = useCallback((personId: number, jobId: number, createdDispatchId?: number) => {
    pushStaffingHistory({
      message: 'H3ightboard assignment saved',
      undo: async () => {
        await unassignMutation.mutateAsync({
          person_id: personId,
          job_id: jobId,
          ...(createdDispatchId != null ? { undo_assign_dispatch_id: createdDispatchId } : {}),
        });
      },
      redo: async () => {
        await foundationAssign(personId, jobId, 'Redo could not be applied because it now requires confirmation.');
      },
    });
  }, [pushStaffingHistory, foundationAssign, unassignMutation]);

  const registerFoundationUnassignUndo = useCallback((personId: number, previousJobId: number) => {
    pushStaffingHistory({
      message: 'H3ightboard assignment removed',
      undo: async () => {
        await restoreFoundationPlacement(personId, previousJobId);
      },
      redo: async () => {
        await unassignMutation.mutateAsync({ person_id: personId, job_id: previousJobId });
      },
    });
  }, [pushStaffingHistory, restoreFoundationPlacement, unassignMutation]);

  const registerFoundationMoveUndo = useCallback((personId: number, previousJobId: number, targetJobId: number) => {
    pushStaffingHistory({
      message: 'H3ightboard move saved',
      undo: async () => {
        await foundationMove(personId, previousJobId, 'Undo could not be applied because it now requires confirmation.', targetJobId);
      },
      redo: async () => {
        await foundationMove(personId, targetJobId, 'Redo could not be applied because it now requires confirmation.', previousJobId);
      },
    });
  }, [pushStaffingHistory, foundationMove]);

  const runBulkBoardRequests = useCallback(async (requests: Promise<unknown>[], failureAction: 'Undo' | 'Redo') => {
    const results = await Promise.allSettled(requests);
    const failedCount = results.filter((result) => result.status === 'rejected').length;
    if (failedCount > 0) {
      const succeededCount = results.length - failedCount;
      throw new Error(
        succeededCount > 0
          ? `${failureAction} partially applied (${succeededCount}/${results.length}). Refreshing board state.`
          : `Failed to ${failureAction.toLowerCase()} H3ightboard changes`,
      );
    }
  }, []);

  const registerBulkBoardUndo = useCallback((
    assigned: BoardAssignment[],
    movedFrom: BoardMoveSnapshot[],
    foundationAssigns: FoundationAssignInfo[] = [],
    foundationMoves: FoundationMoveInfo[] = [],
  ) => {
    if (assigned.length === 0 && movedFrom.length === 0 && foundationAssigns.length === 0 && foundationMoves.length === 0) return;

    pushStaffingHistory({
      message: 'H3ightboard assignment changes saved',
      undo: async () => {
        await runBulkBoardRequests([
          ...assigned.map((assignment) => undoAssign(assignment)),
          ...movedFrom.map((snapshot) => moveAssignment(
            snapshot.newAssignmentId ?? snapshot.previousAssignment.assignment_id ?? snapshot.previousAssignment.id,
            snapshot.previousAssignment.project_id,
            'Undo could not be applied because it now requires confirmation.',
          )),
          ...foundationAssigns.map((fa) => unassignMutation.mutateAsync({
            person_id: fa.person_id,
            job_id: fa.job_id,
            ...(fa.created_dispatch_id != null ? { undo_assign_dispatch_id: fa.created_dispatch_id } : {}),
          })),
          ...foundationMoves.map((fm) => foundationMove(
            fm.person_id,
            fm.previousJobId,
            'Undo could not be applied because it now requires confirmation.',
            fm.targetJobId,
          )),
        ], 'Undo');
      },
      redo: async () => {
        await runBulkBoardRequests([
          ...assigned.map((assignment) => restoreAssignment(assignment)),
          ...movedFrom.map((snapshot) => moveAssignment(
            snapshot.previousAssignment.assignment_id ?? snapshot.previousAssignment.id,
            snapshot.projectId,
            'Redo could not be applied because it now requires confirmation.',
          )),
          ...foundationAssigns.map((fa) => foundationAssign(
            fa.person_id,
            fa.job_id,
            'Redo could not be applied because it now requires confirmation.',
          )),
          ...foundationMoves.map((fm) => foundationMove(
            fm.person_id,
            fm.targetJobId,
            'Redo could not be applied because it now requires confirmation.',
            fm.previousJobId,
          )),
        ], 'Redo');
      },
    });
  }, [foundationAssign, foundationMove, moveAssignment, pushStaffingHistory, restoreAssignment, runBulkBoardRequests, undoAssign, unassignMutation]);

  const registerBulkMoveUndo = useCallback((
    moved: { item: BulkMoveItem; newAssignmentId?: number }[],
    targetName: string,
  ) => {
    if (moved.length === 0) return;
    pushStaffingHistory({
      message: `${moved.length} tech${moved.length === 1 ? '' : 's'} moved to ${targetName}`,
      undo: async () => {
        await runBulkBoardRequests(
          moved.map(({ item, newAssignmentId }) => {
            if (item.isFoundation) {
              return foundationMove(
                item.personId,
                item.previousJobId,
                'Undo could not be applied because it now requires confirmation.',
                item.targetJobId,
              );
            }
            return moveAssignment(
              newAssignmentId ?? item.assignment?.id ?? item.personId,
              item.previousProjectId,
              'Undo could not be applied because it now requires confirmation.',
            );
          }),
          'Undo',
        );
      },
      redo: async () => {
        await runBulkBoardRequests(
          moved.map(({ item }) => {
            if (item.isFoundation) {
              return foundationMove(
                item.personId,
                item.targetJobId,
                'Redo could not be applied because it now requires confirmation.',
                item.previousJobId,
              );
            }
            return moveAssignment(
              item.assignment?.id ?? item.personId,
              item.targetProjectId,
              'Redo could not be applied because it now requires confirmation.',
            );
          }),
          'Redo',
        );
      },
    });
  }, [pushStaffingHistory, runBulkBoardRequests, moveAssignment, foundationMove]);

  const registerBulkRemoveUndo = useCallback((
    removed: BulkRemoveItem[],
    sourceName: string,
  ) => {
    if (removed.length === 0) return;
    pushStaffingHistory({
      message: `${removed.length} tech${removed.length === 1 ? '' : 's'} removed from ${sourceName}`,
      undo: async () => {
        await runBulkBoardRequests(
          removed.map((item) => {
            if (item.isFoundation || !item.assignment) {
              return restoreFoundationPlacement(item.personId, item.previousJobId);
            }
            return restoreAssignment(item.assignment);
          }),
          'Undo',
        );
      },
      redo: async () => {
        await runBulkBoardRequests(
          removed.map((item) => {
            if (item.isFoundation || !item.assignment) {
              return unassignMutation.mutateAsync({ person_id: item.personId, job_id: item.previousJobId });
            }
            return unassignMutation.mutateAsync({ assignment_id: item.assignment.id, person_id: item.personId });
          }),
          'Redo',
        );
      },
    });
  }, [pushStaffingHistory, runBulkBoardRequests, restoreAssignment, restoreFoundationPlacement, unassignMutation]);

  // Build lookup: tech card id → column id(s) from foundation BoardPlacements.
  // person_id → card.id and job_id → column.id maps bridge foundation keys
  // to the IDs used by the rest of the board UI.
  const techAssignments = useMemo(() => {
    if (!data) return new Map<number, number[]>();

    const personToCardId = new Map<number, number>();
    for (const t of data.techs) {
      personToCardId.set(t.person_id, t.id);
    }
    const jobToColId = new Map<number, number>();
    for (const p of data.projects) {
      jobToColId.set(p.job_id, p.id);
    }

    const map = new Map<number, number[]>();
    for (const bp of data.board_placements) {
      const cardId = personToCardId.get(bp.person_id);
      const colId = jobToColId.get(bp.job_id);
      if (cardId !== undefined && colId !== undefined) {
        const existing = map.get(cardId) ?? [];
        if (!existing.includes(colId)) existing.push(colId);
        map.set(cardId, existing);
      }
    }
    return map;
  }, [data]);

  // Stable color for each job (derived from the job id).
  const jobColors = useMemo(() => {
    if (!data) return new Map<number, string>();
    const allJobIds = new Set(data.board_placements.map((bp) => bp.job_id));
    for (const p of data.projects) allJobIds.add(p.job_id);
    const map = new Map<number, string>();
    for (const jobId of allJobIds) {
      map.set(jobId, getJobColor(jobId));
    }
    return map;
  }, [data]);

  // Job id → project name for badge tooltips and confirm messages.
  const jobNameMap = useMemo(() => {
    if (!data) return new Map<number, string>();
    const map = new Map<number, string>();
    for (const p of data.projects) map.set(p.job_id, p.name);
    for (const bp of data.board_placements) {
      if (!map.has(bp.job_id)) map.set(bp.job_id, `Job ${bp.job_id}`);
    }
    return map;
  }, [data]);

  // Job id → project id for resolving assignments during badge-click removal.
  const jobToProjectId = useMemo(() => {
    if (!data) return new Map<number, number>();
    const map = new Map<number, number>();
    for (const p of data.projects) map.set(p.job_id, p.id);
    return map;
  }, [data]);

  // tech card id → active job ids (from BoardPlacements) for badge rendering.
  const techJobAssignments = useMemo(() => {
    if (!data) return new Map<number, number[]>();
    const personToCardId = new Map<number, number>();
    for (const t of data.techs) personToCardId.set(t.person_id, t.id);
    const map = new Map<number, number[]>();
    for (const bp of data.board_placements) {
      const cardId = personToCardId.get(bp.person_id);
      if (cardId === undefined) continue;
      const existing = map.get(cardId) ?? [];
      if (!existing.includes(bp.job_id)) existing.push(bp.job_id);
      map.set(cardId, existing);
    }
    return map;
  }, [data]);

  // Filter techs based on search + filters
  const filteredTechs = useMemo(() => {
    if (!data) return [];
    let techs = data.techs;

    if (search) {
      const q = search.toLowerCase();
      techs = techs.filter(
        (t) =>
          t.first_name.toLowerCase().includes(q) ||
          t.last_name.toLowerCase().includes(q) ||
          `${t.first_name} ${t.last_name}`.toLowerCase().includes(q),
      );
    }

    if (certFilter && crewEnabled) {
      const level = parseInt(certFilter, 10);
      techs = techs.filter((t) => (t.highest_cert_level ?? 0) === level);
    }

    if (skillFilter && crewEnabled) {
      const q = skillFilter.toLowerCase();
      techs = techs.filter((t) =>
        t.skills?.some((s) => s.skill_name.toLowerCase().includes(q)),
      );
    }

    // Sort by cert level descending (Level 3 first, then 2, then 1, then 0)
    techs = [...techs].sort(
      (a, b) => (b.highest_cert_level ?? 0) - (a.highest_cert_level ?? 0),
    );

    return techs;
  }, [data, search, certFilter, skillFilter, crewEnabled]);

  // Co-located projects: group by location_fk_id, keep groups with count >= 2
  const coLocatedMap = useMemo(() => {
    if (!data) return { counts: new Map<number, number>(), projects: new Map<number, { id: number; name: string }[]>() };
    const byLoc = new Map<number, { id: number; name: string }[]>();
    for (const p of data.projects) {
      if (p.location_fk_id == null) continue;
      const entry = byLoc.get(p.location_fk_id);
      if (entry) {
        entry.push({ id: p.id, name: p.name });
      } else {
        byLoc.set(p.location_fk_id, [{ id: p.id, name: p.name }]);
      }
    }
    const counts = new Map<number, number>();
    const projects = new Map<number, { id: number; name: string }[]>();
    for (const [locId, entries] of byLoc) {
      if (entries.length >= 2) {
        counts.set(locId, entries.length);
        projects.set(locId, entries);
      }
    }
    return { counts, projects };
  }, [data]);

  const [coLocatedFilterLocationId, setCoLocatedFilterLocationId] = useState<number | null>(null);

  const timeRangeWindow = useMemo(
    () => getTimeRangeWindow(timeRange, new Date()),
    [data, timeRange],
  );

  // Filter projects
  const filteredProjects = useMemo(() => {
    if (!data) return [];
    let projects = data.projects;
    if (projectsEnabled && projectStatusFilter) {
      projects = projects.filter((p) => p.status === projectStatusFilter);
    }
    if (glanceFilter === 'unstaffed') {
      const staffedColIds = new Set<number>();
      for (const [, colIds] of techAssignments) {
        for (const cid of colIds) staffedColIds.add(cid);
      }
      projects = projects.filter((p) => !staffedColIds.has(p.id));
    }
    if (glanceFilter === 'missing-jsa') {
      projects = projects.filter(
        (p) => p.jsa_status_color === 'red' || p.jsa_status === 'missing',
      );
    }
    if (glanceFilter === 'gear-overdue') {
      projects = projects.filter(hasGearInspectionDue);
    }
    if (glanceFilter === 'gear-failed') {
      projects = projects.filter(hasGearFailed);
    }
    if (glanceFilter === 'gear-life-exceeded') {
      projects = projects.filter(hasGearLifeExceeded);
    }
    if (coLocatedFilterLocationId !== null) {
      projects = projects.filter((p) => p.location_fk_id === coLocatedFilterLocationId);
    }
    return projects;
  }, [data, projectStatusFilter, projectsEnabled, glanceFilter, coLocatedFilterLocationId, techAssignments]);

  // Partition techs by project
  const unassignedTechs = useMemo(() => {
    return filteredTechs.filter((t) => {
      const assignments = techAssignments.get(t.id);
      return !assignments || assignments.length === 0;
    });
  }, [filteredTechs, techAssignments]);

  // Glance counts (derived from full, unfiltered data)
  const glanceCounts = useMemo(() => {
    if (!data) return { unstaffedProjects: 0, unassignedTechs: 0, missingJSAs: 0, blockingCerts: 0, gearOverdue: 0, gearFailed: 0, gearLifeExceeded: 0, payrollAlerts: 0 };
    const staffedColIds = new Set<number>();
    for (const [, colIds] of techAssignments) {
      for (const cid of colIds) staffedColIds.add(cid);
    }
    const unstaffedProjects = data.projects.filter((p) => !staffedColIds.has(p.id)).length;
    const allUnassigned = data.techs.filter((t) => {
      const pids = techAssignments.get(t.id);
      return !pids || pids.length === 0;
    }).length;
    const missingJSAs = data.projects.filter(
      (p) => p.jsa_status_color === 'red' || p.jsa_status === 'missing',
    ).length;
    const blockingCerts = data.techs.filter((t) => t.has_expired_certs === true).length;
    const gearOverdue = data.projects.filter(hasGearInspectionDue).length;
    const gearFailed = data.projects.filter(hasGearFailed).length;
    const gearLifeExceeded = data.projects.filter(hasGearLifeExceeded).length;
    const payrollAlerts = (data.payroll_summary?.timesheets_missing ?? 0) + (data.payroll_summary?.expenses_pending ?? 0);
    return { unstaffedProjects, unassignedTechs: allUnassigned, missingJSAs, blockingCerts, gearOverdue, gearFailed, gearLifeExceeded, payrollAlerts };
  }, [data, techAssignments]);

  // Scroll to unassigned column when "unassigned techs" chip is clicked
  const unassignedColumnRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node && glanceFilter === 'unassigned') {
        node.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    },
    [glanceFilter],
  );

  // Assignments per project
  const assignmentsByProject = useMemo(() => {
    if (!data) return new Map<number, BoardAssignment[]>();
    const map = new Map<number, BoardAssignment[]>();
    for (const a of data.assignments) {
      const existing = map.get(a.project_id) ?? [];
      existing.push(a);
      map.set(a.project_id, existing);
    }
    return map;
  }, [data]);

  const boardProjects = useMemo(() => {
    if (!data) return [];
    if (!timeRangeWindow) return filteredProjects;
    return filteredProjects.filter((project) =>
      isProjectActiveInWindow(assignmentsByProject.get(project.id) ?? [], timeRangeWindow),
    );
  }, [data, filteredProjects, assignmentsByProject, timeRangeWindow]);

  const techsByProject = useMemo(() => {
    const map = new Map<number, BoardTech[]>();
    for (const p of boardProjects) {
      map.set(p.id, []);
    }
    for (const t of filteredTechs) {
      const projectIds = techAssignments.get(t.id);
      if (projectIds) {
        for (const pid of projectIds) {
          const list = map.get(pid);
          if (list) list.push(t);
        }
      }
    }
    return map;
  }, [filteredTechs, boardProjects, techAssignments]);

  const readinessTechsByProject = useMemo(() => {
    const map = new Map<number, BoardTech[]>();
    for (const project of boardProjects) map.set(project.id, []);
    for (const tech of data?.techs ?? []) {
      for (const projectId of techAssignments.get(tech.id) ?? []) {
        map.get(projectId)?.push(tech);
      }
    }
    return map;
  }, [data?.techs, boardProjects, techAssignments]);

  const handleSelectToggle = useCallback((techId: number) => {
    setSelectedTechIds((prev) => {
      const next = new Set(prev);
      if (next.has(techId)) {
        next.delete(techId);
      } else {
        next.add(techId);
      }
      return next;
    });
  }, []);

  const handleDrop = useCallback(
    (item: DragItem, targetProjectId: number | null) => {
      if (!data) return;

      // Resolve the source column from the drag payload. Touch-safe pickers send
      // assignmentId but not sourceProjectId/sourceJobId, so fall back to the
      // assignments list when only assignmentId is present.
      let sourceProjectId = item.sourceProjectId;
      let sourceJobId = item.sourceJobId;
      if (sourceProjectId == null && item.assignmentId != null) {
        const resolvedAssignment = data.assignments.find(
          (a) => a.tech_id === item.techId && (a.assignment_id ?? a.id) === item.assignmentId,
        );
        if (resolvedAssignment != null) {
          sourceProjectId = resolvedAssignment.project_id;
          sourceJobId = data.projects.find((p) => p.id === sourceProjectId)?.job_id;
        }
      }

      // Multi-select drop: if the dragged tech is part of a selection, handle each category
      if (selectedTechIds.size > 1 && selectedTechIds.has(item.techId)) {
        const selectedIds = [...selectedTechIds];
        const sourceProject = sourceProjectId != null
          ? data.projects.find((p) => p.id === sourceProjectId)
          : undefined;
        const sourceProjectName = sourceProject?.name ?? 'this job';
        const sourceJobIdResolved = sourceProject?.job_id;

        const allAssignedToSource = sourceProject != null
          && sourceProjectId != null
          && selectedIds.every((tid) => techAssignments.get(tid)?.includes(sourceProjectId));
        const noneUnassigned = selectedIds.every((tid) => {
          const ids = techAssignments.get(tid);
          return ids != null && ids.length > 0;
        });

        // Pure bulk remove: drag 2+ assigned cards from one column to Unassigned.
        if (targetProjectId === null && allAssignedToSource && noneUnassigned && sourceProjectId != null && sourceJobIdResolved != null) {
          const bulkRemoveItems: BulkRemoveItem[] = [];
          for (const tid of selectedIds) {
            const tech = data.techs.find((t) => t.id === tid);
            if (!tech) continue;
            const assignment = data.assignments.find((a) => a.project_id === sourceProjectId && a.tech_id === tid);
            bulkRemoveItems.push({
              techId: tid,
              personId: tech.person_id,
              name: `${tech.first_name} ${tech.last_name}`,
              previousProjectId: sourceProjectId,
              previousJobId: sourceJobIdResolved,
              assignment,
              isFoundation: assignment == null || !assignment.assignment_id,
            });
          }
          if (bulkRemoveItems.length > 1) {
            enqueuePendingConfirm({
              action: 'bulk-remove',
              title: `Remove ${bulkRemoveItems.length} techs from ${sourceProjectName}?`,
              message: `The following people will be removed from ${sourceProjectName}:`,
              confirmLabel: 'Remove',
              pendingLabel: 'Removing...',
              names: bulkRemoveItems.map((i) => i.name),
              bulkRemoveItems,
            });
            setSelectedTechIds(new Set());
            return;
          }
        }

        // Pure bulk move: drag 2+ assigned cards from one column to a different column.
        if (
          targetProjectId !== null
          && sourceProjectId != null
          && targetProjectId !== sourceProjectId
          && allAssignedToSource
          && noneUnassigned
          && sourceJobIdResolved != null
        ) {
          const targetProject = data.projects.find((p) => p.id === targetProjectId);
          const targetJobId = targetProject?.job_id;
          const noneOnTarget = selectedIds.every((tid) => !techAssignments.get(tid)?.includes(targetProjectId));
          if (targetJobId != null && noneOnTarget) {
            const bulkMoveItems: BulkMoveItem[] = [];
            for (const tid of selectedIds) {
              const tech = data.techs.find((t) => t.id === tid);
              if (!tech) continue;
              const assignment = data.assignments.find((a) => a.project_id === sourceProjectId && a.tech_id === tid);
              bulkMoveItems.push({
                techId: tid,
                personId: tech.person_id,
                name: `${tech.first_name} ${tech.last_name}`,
                previousProjectId: sourceProjectId,
                previousJobId: sourceJobIdResolved,
                targetProjectId,
                targetJobId,
                assignment,
                isFoundation: assignment == null || !assignment.assignment_id,
              });
            }
            if (bulkMoveItems.length > 1) {
              enqueuePendingConfirm({
                action: 'bulk-move',
                title: `Move ${bulkMoveItems.length} techs from ${sourceProjectName} to ${targetProject?.name ?? 'this job'}?`,
                message: `The following people will be moved from ${sourceProjectName} to ${targetProject?.name ?? 'this job'}:`,
                confirmLabel: 'Move',
                pendingLabel: 'Moving...',
                names: bulkMoveItems.map((i) => i.name),
                bulkMoveItems,
              });
              setSelectedTechIds(new Set());
              return;
            }
          }
        }

        // Fallback for mixed groups / multi-select assign / same-column drops.
        if (targetProjectId === null) {
          const selectedSourceIds = new Set<number>();
          let hasUnassignedSelected = false;
          for (const tid of selectedIds) {
            const assignments = techAssignments.get(tid);
            if (assignments && assignments.length > 0) {
              assignments.forEach((id) => selectedSourceIds.add(id));
            } else {
              hasUnassignedSelected = true;
            }
          }
          if (selectedSourceIds.size > 1 || (selectedSourceIds.size > 0 && hasUnassignedSelected)) {
            toast.error("Can't bulk-remove: selected techs are on different jobs");
          }
          setSelectedTechIds(new Set());
          return;
        }

        const targetJobId = data.projects.find((p) => p.id === targetProjectId)?.job_id;
        if (targetJobId == null) return;
        const toAssign: number[] = [];
        const toAssignFoundation: { person_id: number; job_id: number | undefined }[] = [];
        const toMove: { assignment_id?: number; project_id?: number; person_id?: number; job_id?: number; from_job_id?: number }[] = [];
        const moveSnapshots: (BoardMoveSnapshot | null)[] = [];
        const foundationMoveInfos: (FoundationMoveInfo | null)[] = [];

        for (const tid of selectedTechIds) {
          const projectIds = techAssignments.get(tid);
          const tech = data.techs.find((t) => t.id === tid);
          if (!projectIds || projectIds.length === 0) {
            if (tech && 'tech_id' in tech) {
              // Module-backed tech: use bulk_assign with TechProfile PK
              toAssign.push(tid);
            } else if (tech) {
              // Foundation-only: use individual assign with person_id + job_id
              toAssignFoundation.push({ person_id: tech.person_id, job_id: targetJobId });
            }
          } else if (projectIds.includes(targetProjectId)) {
            // Already on target — skip
          } else {
            // Determine the source column/job for this tech. Prefer the dragged
            // card's source; if the tech lives elsewhere, use its only column.
            let techSourceProjectId: number | undefined;
            let techSourceJobId: number | undefined;
            if (sourceProjectId != null && projectIds.includes(sourceProjectId)) {
              techSourceProjectId = sourceProjectId;
              techSourceJobId = sourceJobId;
            } else if (projectIds.length === 1) {
              techSourceProjectId = projectIds[0];
              const sourceProject = data.projects.find((p) => p.id === techSourceProjectId);
              techSourceJobId = sourceProject?.job_id;
            }

            if (techSourceProjectId == null || techSourceJobId == null || !tech) {
              // Cannot safely determine the source; skip this tech.
              continue;
            }

            const assignment = data.assignments.find(
              (a) => a.tech_id === tid && a.project_id === techSourceProjectId,
            );
            if (assignment && assignment.assignment_id) {
              // Module-backed: the crew.Assignment id identifies the source.
              toMove.push({
                assignment_id: assignment.assignment_id,
                project_id: targetProjectId,
                person_id: tech.person_id,
                job_id: targetJobId,
                from_job_id: techSourceJobId,
              });
              moveSnapshots.push({ previousAssignment: assignment, projectId: targetProjectId });
              foundationMoveInfos.push(null);
            } else {
              // Foundation-only card: identify the source by job id.
              toMove.push({
                person_id: tech.person_id,
                project_id: targetProjectId,
                job_id: targetJobId,
                from_job_id: techSourceJobId,
              });
              moveSnapshots.push(null);
              foundationMoveInfos.push({
                person_id: tech.person_id,
                previousJobId: techSourceJobId,
                targetJobId,
              });
            }
          }
        }

        const assignPromise = toAssign.length > 0
          ? bulkAssignMutation.mutateAsync({ project_id: targetProjectId, tech_ids: toAssign }).then((result) => {
              if (isConfirmRequired<BulkConfirmRequiredResponse>(result)) {
                return [] as BoardAssignment[];
              }
              if (result.errors.length > 0) {
                toast.error(`${result.errors.length} tech(s) failed to assign`);
              }
              if (boardHasMounted.current) {
                setDroppedTechIds((prev) => new Set([...prev, ...result.assigned.map((a) => a.tech_id)]));
              }
              return result.assigned;
            })
          : Promise.resolve([] as BoardAssignment[]);

        // Foundation-only techs: assign individually via person_id + job_id
        const foundationAssignPromise = toAssignFoundation.length > 0
          ? Promise.allSettled(
              toAssignFoundation.map((fa) =>
                assignMutation.mutateAsync({ person_id: fa.person_id, job_id: fa.job_id }),
              ),
            ).then((results) => {
              const failed = results.filter((r) => r.status === 'rejected').length;
              if (failed > 0) toast.error(`${failed} foundation tech(s) failed to assign`);
              const successfulAssigns = results
                .map((r, i): FoundationAssignInfo | null => {
                  if (r.status !== 'fulfilled' || isConfirmRequired(r.value)) return null;
                  const entry = toAssignFoundation[i];
                  if (entry == null || entry.job_id == null) return null;
                  const resp = r.value as Partial<BoardAssignment>;
                  return {
                    person_id: entry.person_id,
                    job_id: entry.job_id,
                    ...(resp.dispatch_created && resp.assignment_id != null
                      ? { created_dispatch_id: resp.assignment_id }
                      : {}),
                  };
                })
                .filter((entry): entry is FoundationAssignInfo => entry != null);
              return successfulAssigns;
            })
          : Promise.resolve([] as FoundationAssignInfo[]);

        const movePromise = toMove.length > 0
          ? Promise.allSettled(toMove.map((mv) => moveMutation.mutateAsync(mv))).then((results) => {
              const successfulSnapshots = results
                .map((r, i) => {
                  if (r.status !== 'fulfilled' || isConfirmRequired(r.value)) return null;
                  const snapshot = moveSnapshots[i];
                  if (snapshot == null) return null;
                  if ('assignment_id' in r.value && r.value.assignment_id != null) {
                    return { ...snapshot, newAssignmentId: r.value.assignment_id as number };
                  }
                  return snapshot;
                })
                .filter((snapshot): snapshot is BoardMoveSnapshot => snapshot != null);
              const successfulFoundationMoves = results
                .map((r, i) => r.status === 'fulfilled' && !isConfirmRequired(r.value) ? foundationMoveInfos[i] : null)
                .filter((fm): fm is FoundationMoveInfo => fm != null);
              if (boardHasMounted.current) {
                setDroppedTechIds((prev) => new Set([
                  ...prev,
                  ...successfulSnapshots.map((snapshot) => snapshot.previousAssignment.tech_id),
                ]));
              }
              return { moduleSnapshots: successfulSnapshots, foundationMoves: successfulFoundationMoves };
            })
          : Promise.resolve({ moduleSnapshots: [] as BoardMoveSnapshot[], foundationMoves: [] as FoundationMoveInfo[] });

        Promise.all([assignPromise, foundationAssignPromise, movePromise])
          .then(([assigned, foundationAssigned, moveResult]) => {
            registerBulkBoardUndo(assigned, moveResult.moduleSnapshots, foundationAssigned, moveResult.foundationMoves);
          })
          .catch(() => {
            toast.error('Some H3ightboard changes failed');
          })
          .finally(() => setSelectedTechIds(new Set()));

        return;
      }

      const currentAssignments = techAssignments.get(item.techId);
      const isCurrentlyAssigned = currentAssignments && currentAssignments.length > 0;

      // Resolve the target column's foundation job_id
      const targetJobId = targetProjectId != null
        ? data.projects.find((p) => p.id === targetProjectId)?.job_id
        : undefined;

      // Resolve the source assignment/column from the drag payload so a person
      // on multiple jobs is removed/moved from the exact column they dragged.
      const sourceAssignment = sourceProjectId != null
        ? data.assignments.find((a) => a.project_id === sourceProjectId && a.tech_id === item.techId)
        : undefined;

      if (targetProjectId === null) {
        // Already unassigned → no-op
        if (!isCurrentlyAssigned) return;
        // Drag off a job column → ask before removing this one placement only.
        if (!sourceAssignment && sourceJobId === undefined) return;
        const tech = data.techs.find((t) => t.id === item.techId);
        const resolvedProjectId = sourceProjectId ?? sourceAssignment?.project_id;
        const sourceProject = resolvedProjectId != null ? data.projects.find((p) => p.id === resolvedProjectId) : undefined;
        const techName = tech ? `${tech.first_name} ${tech.last_name}` : 'This person';
        const projectName = sourceProject?.name ?? 'this job';
        enqueuePendingConfirm({
          action: 'unassign',
          title: `Remove ${techName} from ${projectName}?`,
          message: removeConfirmMessage(techName, projectName, sourceAssignment),
          confirmLabel: 'Remove',
          pendingLabel: 'Removing...',
          unassignPayload: sourceAssignment
            ? { assignment_id: sourceAssignment.id, person_id: item.personId }
            : { person_id: item.personId, job_id: sourceJobId },
          previousAssignment: sourceAssignment,
          previousJobId: sourceJobId,
          techName,
          projectName,
        });
      } else if (!isCurrentlyAssigned) {
        // Drop from unassigned → project column → assign
        assignMutation.mutate({
          person_id: item.personId,
          job_id: targetJobId,
          tech_id: item.techId,
          project_id: targetProjectId,
        }, {
          onSuccess: (respData) => {
            if (isConfirmRequired(respData)) return;
            if (boardHasMounted.current) setDroppedTechIds((prev) => new Set(prev).add(item.techId));
            if ('id' in respData && respData.id) {
              registerAssignUndo(respData as BoardAssignment);
            } else if (targetJobId !== undefined) {
              registerFoundationAssignUndo(item.personId, targetJobId);
            }
          },
        });
      } else if (isCurrentlyAssigned) {
        // Person already has at least one active placement.
        const alreadyOnTarget = targetProjectId != null && currentAssignments?.includes(targetProjectId);
        if (sourceAssignment?.project_id === targetProjectId || alreadyOnTarget) {
          // Same target column or already on target — no-op
        } else if (targetProjectId != null) {
          // Double-book: add to target job while keeping existing placements.
          const currentProjectId =
            sourceProjectId != null && currentAssignments?.includes(sourceProjectId)
              ? sourceProjectId
              : currentAssignments?.[0];
          const tech = data.techs.find((t) => t.id === item.techId);
          const currentProject = currentProjectId != null ? data.projects.find((p) => p.id === currentProjectId) : undefined;
          const targetProject = data.projects.find((p) => p.id === targetProjectId);
          const techName = tech ? `${tech.first_name} ${tech.last_name}` : 'This person';
          const currentName = currentProject?.name ?? 'another job';
          const targetName = targetProject?.name ?? 'this job';
          enqueuePendingConfirm({
            action: 'double-book',
            title: 'Add to another job?',
            message: `${techName} is on ${currentName}. Add them to ${targetName} too?`,
            confirmLabel: 'Add',
            assignPayload: {
              person_id: item.personId,
              job_id: targetJobId,
              tech_id: item.techId,
              project_id: targetProjectId,
            },
          });
        }
      }

      setSelectedTechIds(new Set());
    },
    [
      data,
      techAssignments,
      selectedTechIds,
      enqueuePendingConfirm,
      assignMutation,
      moveMutation,
      bulkAssignMutation,
      boardHasMounted,
      registerAssignUndo,
      registerMoveUndo,
      registerFoundationAssignUndo,
      registerFoundationMoveUndo,
      registerBulkBoardUndo,
    ],
  );

  const handleDropAnimationEnd = useCallback((techId: number) => {
    setDroppedTechIds((prev) => {
      const next = new Set(prev);
      next.delete(techId);
      return next;
    });
  }, []);

  const handleBadgeClick = useCallback((techId: number, jobId: number) => {
    if (!data) return;
    const tech = data.techs.find((t) => t.id === techId);
    if (!tech) return;
    const projectId = jobToProjectId.get(jobId);
    const assignment = projectId !== undefined
      ? data.assignments.find((a) => a.project_id === projectId && a.tech_id === techId)
      : undefined;
    const techName = `${tech.first_name} ${tech.last_name}`;
    const projectName = jobNameMap.get(jobId) ?? 'this job';
    enqueuePendingConfirm({
      action: 'unassign',
      title: `Remove ${techName} from ${projectName}?`,
      message: removeConfirmMessage(techName, projectName, assignment),
      confirmLabel: 'Remove',
      pendingLabel: 'Removing...',
      unassignPayload: assignment
        ? { assignment_id: assignment.id, person_id: tech.person_id }
        : { person_id: tech.person_id, job_id: jobId },
      previousAssignment: assignment,
      previousJobId: assignment ? undefined : jobId,
      techName,
      projectName,
    });
  }, [data, jobNameMap, jobToProjectId, enqueuePendingConfirm]);

  const handleAssignCrewList = useCallback(
    (projectId: number, techIds: number[]) => {
      bulkAssignMutation.mutate(
        { project_id: projectId, tech_ids: techIds },
        {
          onSuccess: (result) => {
            if (isConfirmRequired<BulkConfirmRequiredResponse>(result)) return;
            if (result.errors.length > 0) {
              toast.error(`${result.errors.length} tech(s) failed to assign`);
            } else if (result.assigned.length > 0) {
              registerBulkBoardUndo(result.assigned, []);
            }
          },
        },
      );
    },
    [bulkAssignMutation, registerBulkBoardUndo],
  );

  const handleConfirm = useCallback((overrideReason?: string) => {
    if (!pendingConfirm) return;
    if ((pendingConfirm.action === 'assign' || pendingConfirm.action === 'double-book') && pendingConfirm.assignPayload) {
      const isDoubleBook = pendingConfirm.action === 'double-book';
      const techId = pendingConfirm.assignPayload.tech_id ?? pendingConfirm.assignPayload.person_id;
      const payload = pendingConfirm.action === 'assign'
        ? {
          ...pendingConfirm.assignPayload,
          confirmed: true,
          confirmation_token: pendingConfirm.confirmationToken,
          override_reason: overrideReason,
        }
        : pendingConfirm.assignPayload;
      assignMutation.mutate(payload, {
        onSuccess: (respData) => {
          if (isConfirmRequired(respData)) {
            if (isDoubleBook) {
              dequeuePendingConfirm(pendingConfirm.id);
            }
            return;
          }
          if (techId != null && boardHasMounted.current) setDroppedTechIds((prev) => new Set(prev).add(techId));
          if ('id' in respData && respData.id) {
            registerAssignUndo(respData as BoardAssignment);
          } else if (
            pendingConfirm.assignPayload?.person_id != null
            && pendingConfirm.assignPayload?.job_id != null
          ) {
            registerFoundationAssignUndo(
              pendingConfirm.assignPayload.person_id,
              pendingConfirm.assignPayload.job_id,
            );
          }
          if (pendingConfirm.action === 'double-book') {
            const tech = data?.techs.find((t) => t.id === pendingConfirm.assignPayload?.tech_id);
            const techName = tech ? `${tech.first_name} ${tech.last_name}` : 'Person';
            const targetProject = data?.projects.find((p) => p.id === pendingConfirm.assignPayload?.project_id);
            const targetName = targetProject?.name ?? 'the job';
            toast.success(`${techName} added to ${targetName}.`);
          }
          dequeuePendingConfirm(pendingConfirm.id);
        },
        onError: (error) => {
          if (!isDoubleBook) {
            if (isConfirmationExpired(error)) {
              toast.error('Confirmation expired — please try that change again');
              dequeuePendingConfirm(pendingConfirm.id);
            } else {
              toast.error('Unable to confirm assignment. Please try again.');
            }
          } else {
            toast.error('Unable to add technician to the job');
          }
        },
      });
    } else if (pendingConfirm.action === 'move' && pendingConfirm.movePayload) {
      const projectId = pendingConfirm.movePayload.project_id;
      moveMutation.mutate({
        ...pendingConfirm.movePayload,
        confirmed: true,
        confirmation_token: pendingConfirm.confirmationToken,
        override_reason: overrideReason,
      }, {
        onSuccess: (respData) => {
          if (isConfirmRequired(respData)) return;
          const newAssignmentId = 'id' in respData && respData.id ? respData.id as number : undefined;
          const a = pendingConfirm.previousAssignment;
          if (a && boardHasMounted.current) setDroppedTechIds((prev) => new Set(prev).add(a.tech_id));
          if (a && projectId != null) {
            registerMoveUndo(a, projectId, newAssignmentId);
          } else if (
            pendingConfirm.movePayload?.person_id != null
            && pendingConfirm.previousJobId != null
            && pendingConfirm.movePayload?.job_id != null
          ) {
            registerFoundationMoveUndo(
              pendingConfirm.movePayload.person_id,
              pendingConfirm.previousJobId,
              pendingConfirm.movePayload.job_id,
            );
            const tech = data?.techs.find((t) => t.person_id === pendingConfirm.movePayload?.person_id);
            if (tech && boardHasMounted.current) setDroppedTechIds((prev) => new Set(prev).add(tech.id));
          }
          dequeuePendingConfirm(pendingConfirm.id);
        },
        onError: (error) => {
          if (isConfirmationExpired(error)) {
            toast.error('Confirmation expired — please try that change again');
            dequeuePendingConfirm(pendingConfirm.id);
          } else {
            toast.error('Unable to confirm move. Please try again.');
          }
        },
      });
    } else if (pendingConfirm.action === 'bulk-assign' && pendingConfirm.bulkAssignPayload) {
      bulkAssignMutation.mutate({
        ...pendingConfirm.bulkAssignPayload,
        confirmed: true,
        confirmation_token: pendingConfirm.confirmationToken,
        override_reason: overrideReason,
      }, {
        onSuccess: (result) => {
          if (isConfirmRequired<BulkConfirmRequiredResponse>(result)) return;
          if (result.errors.length > 0) {
            toast.error(`${result.errors.length} tech(s) failed to assign`);
          }
          if (boardHasMounted.current) {
            setDroppedTechIds((prev) => new Set([
              ...prev,
              ...result.assigned.map((assignment) => assignment.tech_id),
            ]));
          }
          if (result.assigned.length > 0) {
            registerBulkBoardUndo(result.assigned, []);
          }
          dequeuePendingConfirm(pendingConfirm.id);
        },
        onError: (error) => {
          if (isConfirmationExpired(error)) {
            toast.error('Confirmation expired — please try that change again');
            dequeuePendingConfirm(pendingConfirm.id);
          } else {
            toast.error('Unable to confirm bulk assignment. Please try again.');
          }
        },
      });
    } else if (pendingConfirm.action === 'unassign' && pendingConfirm.unassignPayload) {
      const { unassignPayload, previousAssignment, previousJobId, techName, projectName } = pendingConfirm;
      unassignMutation.mutate(unassignPayload, {
        onSuccess: () => {
          const name = techName ?? 'Person';
          const job = projectName ?? 'the job';
          toast.success(`${name} removed from ${job}.`);
          if (previousAssignment) {
            if (boardHasMounted.current) setDroppedTechIds((prev) => new Set(prev).add(previousAssignment.tech_id));
            registerUnassignUndo(previousAssignment);
          } else if (previousJobId != null && unassignPayload.person_id != null) {
            const tech = data?.techs.find((t) => t.person_id === unassignPayload.person_id);
            if (tech && boardHasMounted.current) setDroppedTechIds((prev) => new Set(prev).add(tech.id));
            registerFoundationUnassignUndo(unassignPayload.person_id, previousJobId);
          }
          dequeuePendingConfirm(pendingConfirm.id);
        },
      });
    } else if (pendingConfirm.action === 'bulk-move' && pendingConfirm.bulkMoveItems && pendingConfirm.bulkMoveItems.length > 0) {
      const { bulkMoveItems } = pendingConfirm;
      const targetProject = data?.projects.find((p) => p.id === bulkMoveItems[0].targetProjectId);
      const targetName = targetProject?.name ?? 'the job';

      const runBulkMove = async () => {
        const moved: { item: BulkMoveItem; newAssignmentId?: number }[] = [];
        let awaitingConfirmation = 0;
        for (const item of bulkMoveItems) {
          const payload = item.assignment?.id
            ? {
              assignment_id: item.assignment.id,
              project_id: item.targetProjectId,
              person_id: item.personId,
              job_id: item.targetJobId,
              from_job_id: item.previousJobId,
            }
            : {
              person_id: item.personId,
              project_id: item.targetProjectId,
              job_id: item.targetJobId,
              from_job_id: item.previousJobId,
            };
          try {
            const resp = await undoMoveMutation.mutateAsync(payload);
            if (isConfirmRequired(resp)) {
              awaitingConfirmation++;
              enqueuePendingConfirm({
                action: 'move',
                warnings: resp.warnings,
                confirmationToken: resp.confirmation_token,
                canOverride: resp.can_override,
                affectedNames: [item.name],
                confirmLabel: 'Move',
                pendingLabel: 'Moving...',
                movePayload: payload,
                previousAssignment: item.assignment,
                previousJobId: item.previousJobId,
              });
              continue;
            }
            const newAssignmentId = 'id' in resp && resp.id ? resp.id as number : undefined;
            moved.push({ item, newAssignmentId });
          } catch {
            // Individual failure is reported below via count mismatch.
          }
        }

        if (moved.length > 0) {
          if (boardHasMounted.current) {
            setDroppedTechIds((prev) => new Set([...prev, ...moved.map(({ item }) => item.techId)]));
          }
          registerBulkMoveUndo(moved, targetName);
        }
        const failedCount = bulkMoveItems.length - moved.length - awaitingConfirmation;
        if (failedCount > 0) {
          toast.error(`${failedCount} tech(s) failed to move`);
        }
        dequeuePendingConfirm(pendingConfirm.id);
      };
      runBulkMove();
    } else if (pendingConfirm.action === 'bulk-remove' && pendingConfirm.bulkRemoveItems && pendingConfirm.bulkRemoveItems.length > 0) {
      const { bulkRemoveItems } = pendingConfirm;
      const sourceProject = data?.projects.find((p) => p.id === bulkRemoveItems[0].previousProjectId);
      const sourceName = sourceProject?.name ?? 'this job';

      const runBulkRemove = async () => {
        const removed: BulkRemoveItem[] = [];
        for (const item of bulkRemoveItems) {
          const payload = item.assignment?.id
            ? { assignment_id: item.assignment.id, person_id: item.personId }
            : { person_id: item.personId, job_id: item.previousJobId };
          try {
            await unassignMutation.mutateAsync(payload);
            removed.push(item);
          } catch {
            // Individual failure is reported below via count mismatch.
          }
        }

        if (removed.length > 0) {
          if (boardHasMounted.current) {
            setDroppedTechIds((prev) => new Set([...prev, ...removed.map((r) => r.techId)]));
          }
          registerBulkRemoveUndo(removed, sourceName);
        }
        if (removed.length < bulkRemoveItems.length) {
          toast.error(`${bulkRemoveItems.length - removed.length} tech(s) failed to remove`);
        }
        dequeuePendingConfirm(pendingConfirm.id);
      };
      runBulkRemove();
    }
  }, [pendingConfirm, assignMutation, bulkAssignMutation, moveMutation, undoMoveMutation, unassignMutation, data, boardHasMounted, registerAssignUndo, registerMoveUndo, registerUnassignUndo, registerBulkBoardUndo, registerBulkMoveUndo, registerBulkRemoveUndo, registerFoundationAssignUndo, registerFoundationMoveUndo, registerFoundationUnassignUndo, enqueuePendingConfirm, dequeuePendingConfirm]);

  const handleCancelConfirm = useCallback(() => {
    if (
      pendingConfirm
      && pendingConfirm.affectedNames
      && pendingConfirm.affectedNames.length > 0
      && (pendingConfirm.action === 'assign'
        || pendingConfirm.action === 'move'
        || pendingConfirm.action === 'bulk-assign')
    ) {
      const verb = pendingConfirm.action === 'move' ? 'moved' : 'assigned';
      const subjectVerb = pendingConfirm.affectedNames.length === 1 ? 'was' : 'were';
      toast.error(`${pendingConfirm.affectedNames.join(', ')} ${subjectVerb} not ${verb}.`);
    }
    if (pendingConfirm) {
      dequeuePendingConfirm(pendingConfirm.id);
    }
  }, [dequeuePendingConfirm, pendingConfirm]);

  const isMutating =
    assignMutation.isPending ||
    unassignMutation.isPending ||
    restoreAssignmentMutation.isPending ||
    moveMutation.isPending ||
    undoMoveMutation.isPending ||
    undoAssignMutation.isPending ||
    bulkAssignMutation.isPending;

  const handleDispatched = useCallback((count: number, projectCount: number) => {
    const pNoun = projectCount !== 1
      ? terminology.project_label_plural.toLowerCase()
      : terminology.project_label.toLowerCase();
    toast.success(
      `H3ightboard dispatched to ${count} tech${count !== 1 ? 's' : ''} across ${projectCount} ${pNoun}`,
      {
        action: { label: 'View status', onClick: () => setHistoryDrawerOpen(true) },
      },
    );
  }, [setHistoryDrawerOpen, terminology]);

  const unsavedChangesGuard = useH3ightboardUnsavedChangesGuard({
    hasUnsavedChanges: pendingConfirm !== null || isMutating,
    canSave: pendingConfirm !== null,
    isSaving: isMutating,
    onSave: handleConfirm,
    onDiscard: handleCancelConfirm,
  });

  useKeyboardShortcuts({
    searchInputRef,
    clearSelection: useCallback(() => setSelectedTechIds(new Set()), []),
    openDispatch: useCallback(() => {
      if (hasUndispatchedPlacements) setDispatchOpen(true);
    }, [hasUndispatchedPlacements, setDispatchOpen]),
    toggleShortcutOverlay: useCallback(() => setShortcutOverlayOpen((o) => !o), [setShortcutOverlayOpen]),
    undo: useCallback(() => runStaffingHistory('undo'), [runStaffingHistory]),
    redo: useCallback(() => runStaffingHistory('redo'), [runStaffingHistory]),
    canEdit,
    canDispatch: canDispatch && hasUndispatchedPlacements,
    isDialogOpen: dispatchOpen || historyDrawerOpen || shortcutOverlayOpen || pendingConfirm !== null || unsavedChangesGuard.open,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">Failed to load H3ightboard.</p>
      </div>
    );
  }

  return (
    <DndProvider options={HTML5toTouch}>
      <div className="flex flex-col h-auto min-h-[calc(100dvh-64px-3.5rem)] sm:min-h-[calc(100dvh-64px-4rem)] lg:h-[calc(100dvh-64px)] lg:min-h-0">
        {/* Print-only header (desktop) */}
        <div className="hidden print:block px-6 py-4 border-b" data-print-hide-pocket>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-black">{orgName || 'Organization'}</h1>
              <h2 className="text-lg font-semibold text-gray-700">H3ightboard</h2>
            </div>
            <div className="text-sm text-gray-600">
              {formatDateOnly(orgToday, '—', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </div>
          </div>
        </div>

        {/* Print-only header (A6 pocket) */}
        <div className="hidden print:block" data-print-pocket-header>
          <h1 className="text-sm font-bold text-black">
            H3ightboard &mdash;{' '}
            {formatDateOnly(orgToday, '—', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
            {' '}&mdash; {orgName || 'Organization'}
          </h1>
        </div>

        {/* Read-only banner for viewer persona */}
        {personaDefaults.isReadOnly && !canEdit && (
          <div
            className="mx-6 mt-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 print:hidden"
            data-testid="read-only-banner"
          >
            <ShieldAlert className="h-4 w-4 flex-shrink-0" />
            <span>{personaDefaults.readOnlyReason}</span>
          </div>
        )}

        {/* Limited-access banner for supervisor/inspector persona */}
        {personaDefaults.limitedAccessReason && !canEdit && (
          <div
            className="mx-6 mt-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 print:hidden"
            data-testid="supervisor-limited-banner"
          >
            <ShieldAlert className="h-4 w-4 flex-shrink-0" />
            <span>{personaDefaults.limitedAccessReason}</span>
          </div>
        )}

        {/* Demo walkthrough checklist */}
        <DemoChecklistBanner />

        {/* Executive rollup banner (Owner persona only) */}
        {SHOW_BOARD_SUMMARY_ROWS && isOwnerPersona && rollupData && (
          <div className="px-3 md:px-6 pt-3 print:hidden">
            <ExecutiveRollupBanner rollup={rollupData} terminology={terminology} />
          </div>
        )}

        {/* Standalone onboarding: shown only when no modules are enabled */}
        {data && !crewEnabled && !projectsEnabled && !safetyEnabled && !inventoryEnabled && !payrollEnabled && !complianceEnabled && (
          <div
            className="mx-3 md:mx-6 mt-3 flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 print:hidden"
            data-testid="standalone-onboarding-banner"
          >
            <LayoutGrid className="h-5 w-5 flex-shrink-0 text-blue-500" />
            <div>
              <p className="font-medium">H3ightboard is ready</p>
              <p className="text-xs text-blue-600 mt-0.5">
                Drag people onto jobs to organize your crew. Signals and tracking begin when you add modules.
              </p>
            </div>
          </div>
        )}

        {/* At-a-glance banner */}
        {SHOW_BOARD_SUMMARY_ROWS && (
        <TodayAtAGlanceBanner
          counts={glanceCounts}
          activeFilter={glanceFilter}
          onFilterClick={(filter) => {
            setGlanceFilter(filter);
            markManualOverride();
            if (filter !== null) setCoLocatedFilterLocationId(null);
          }}
          terminology={terminology}
          safetyEnabled={safetyEnabled}
          crewEnabled={crewEnabled}
          inventoryEnabled={inventoryEnabled}
          payrollEnabled={payrollEnabled}
          showFinancials={!!data?.payroll_summary}
        />
        )}

        {/* Header */}
        <div className="px-3 py-2 md:px-6 md:py-4 border-b bg-white flex-shrink-0 print:hidden">
          <div className="flex items-center justify-between gap-2 md:gap-4">
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-gray-900"><img src="/logos/h3ightboard.svg" alt="H3ightboard" className="h-7" /></h1>
              <p className="text-sm text-gray-500 truncate">
                {personaDefaults.boardSubtitle}
              </p>
              {presenceViewers.length > 0 && (
                <TooltipProvider>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Viewing now
                    </span>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {presenceViewers.map((viewer) => (
                        <Tooltip key={viewer.user_id}>
                          <TooltipTrigger asChild>
                            <div
                              className="inline-flex h-8 min-w-8 items-center justify-center rounded-full border border-blue-200 bg-blue-50 px-2 text-xs font-semibold text-blue-700 shadow-sm"
                              aria-label={viewer.name}
                            >
                              {viewer.initials}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{viewer.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                </TooltipProvider>
              )}
            </div>

            <div className="flex items-center gap-1.5 md:gap-3 flex-wrap justify-end">
              {/* View toggle */}
              <div className="flex items-center rounded-lg border border-gray-300 overflow-hidden">
                <button
                  onClick={() => setActiveView('board')}
                  className={`inline-flex items-center gap-1.5 px-2 py-1.5 md:px-3 text-sm transition-colors ${
                    activeView === 'board'
                      ? 'bg-blue-50 text-blue-700'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <LayoutGrid className="h-4 w-4" />
                  <span className="hidden md:inline">Board</span>
                </button>
                <button
                  onClick={() => setActiveView('timeline')}
                  className={`inline-flex items-center gap-1.5 px-2 py-1.5 md:px-3 text-sm transition-colors border-l border-gray-300 ${
                    activeView === 'timeline'
                      ? 'bg-blue-50 text-blue-700'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <CalendarDays className="h-4 w-4" />
                  <span className="hidden md:inline">Timeline</span>
                </button>
              </div>

              <div
                className="flex items-center rounded-lg border border-gray-300 overflow-hidden"
                data-testid="board-time-range-tabs"
              >
                {([
                  ['all', 'All'],
                  ['today', 'Today'],
                  ['tomorrow', 'Tomorrow'],
                  ['week', 'This week'],
                ] as const).map(([value, label], index) => (
                  <button
                    key={value}
                    type="button"
                    data-testid={`time-range-${value}`}
                    aria-pressed={timeRange === value}
                    onClick={() => {
                      setTimeRange(value);
                      markManualOverride();
                    }}
                    className={`inline-flex items-center gap-1.5 px-2 py-1.5 md:px-3 text-sm transition-colors ${
                      timeRange === value
                        ? 'bg-blue-50 text-blue-700'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    } ${index > 0 ? 'border-l border-gray-300' : ''}`}
                  >
                    <span className="hidden md:inline">{label}</span>
                    <span className="md:hidden">{label === 'This week' ? 'Week' : label}</span>
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search techs..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 pr-3 py-2 md:py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-36 md:w-48"
                />
              </div>

              {/* Filter toggle */}
              <button
                onClick={() => {
                  setShowFilters((f) => !f);
                  markManualOverride();
                }}
                className={`inline-flex items-center gap-1.5 px-2 py-2 md:px-3 md:py-1.5 text-sm rounded-lg border transition-colors ${
                  showFilters
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="h-4 w-4" />
                <span className="hidden md:inline">Filters</span>
              </button>

              {/* Dispatch button — Supervisor+ can preflight/dispatch */}
              {canDispatch && (
                <>
                  <button
                    onClick={() => setHistoryDrawerOpen(true)}
                    className="inline-flex items-center gap-1.5 px-2 py-2 md:px-3 md:py-1.5 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <History className="h-4 w-4" />
                    <span className="hidden md:inline">History</span>
                  </button>
                  <button
                    onClick={() => setDispatchOpen(true)}
                    disabled={!hasUndispatchedPlacements}
                    data-testid="dispatch-button"
                    data-readiness={dispatchButtonState}
                    className={`inline-flex items-center gap-1.5 px-2 py-2 md:px-3 md:py-1.5 text-sm rounded-lg transition-colors ${dispatchButtonClass}`}
                  >
                    <ClipboardCheck className="h-4 w-4" />
                    <span className="hidden md:inline">Dispatch</span>
                  </button>
                </>
              )}

              {/* Supervision cockpit toggle (Supervisor+ only) */}
              {canViewSupervision && (
                <button
                  onClick={() => setSupervisionPanelOpen((o) => !o)}
                  className={`inline-flex items-center gap-1.5 px-2 py-2 md:px-3 md:py-1.5 text-sm rounded-lg border transition-colors ${
                    supervisionPanelOpen
                      ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid="supervision-cockpit-toggle"
                >
                  <Eye className="h-4 w-4" />
                  <span className="hidden md:inline">Supervision</span>
                </button>
              )}

              {/* Compliance evidence panel toggle */}
              {(safetyEnabled || crewEnabled || inventoryEnabled) && (
                <button
                  onClick={() => setEvidencePanelOpen((o) => !o)}
                  className={`inline-flex items-center gap-1.5 px-2 py-2 md:px-3 md:py-1.5 text-sm rounded-lg border transition-colors ${
                    evidencePanelOpen
                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid="compliance-evidence-toggle"
                >
                  <ShieldAlert className="h-4 w-4" />
                  <span className="hidden md:inline">Evidence</span>
                </button>
              )}

              {/* Print / Export buttons — hidden on touch */}
              {!isTouchDevice && (
                <TooltipProvider>
                  <button
                    onClick={() => window.print()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Printer className="h-4 w-4" />
                    Print Board
                  </button>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => window.print()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <FileDown className="h-4 w-4" />
                        Print / Save as PDF
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Use &quot;Save as PDF&quot; in the print dialog</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {isMutating && (
                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
              )}
            </div>
          </div>

          {/* Expanded filters */}
          {showFilters && (
            <div className="mt-3 flex items-center gap-2 md:gap-4 flex-wrap">
              {crewEnabled && (
                <>
                  <select
                    value={certFilter}
                    onChange={(e) => { setCertFilter(e.target.value); markManualOverride(); }}
                    className="text-sm border border-gray-300 rounded-lg px-2 py-2 md:py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All cert levels</option>
                    <option value="3">Level 3</option>
                    <option value="2">Level 2</option>
                    <option value="1">Level 1</option>
                    <option value="0">No cert</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Filter by skill..."
                    value={skillFilter}
                    onChange={(e) => { setSkillFilter(e.target.value); markManualOverride(); }}
                    className="text-sm border border-gray-300 rounded-lg px-2 py-2 md:py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 w-40"
                  />
                </>
              )}
              {projectsEnabled && (
                <select
                  value={projectStatusFilter}
                  onChange={(e) => { setProjectStatusFilter(e.target.value); markManualOverride(); }}
                  className="text-sm border border-gray-300 rounded-lg px-2 py-2 md:py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All {terminology.project_label.toLowerCase()} statuses</option>
                  <option value="active">Active</option>
                  <option value="awarded">Awarded</option>
                </select>
              )}
              <button
                onClick={() => {
                  setCertFilter('');
                  setSkillFilter('');
                  setProjectStatusFilter('');
                  setGlanceFilter(null);
                  setCoLocatedFilterLocationId(null);
                  setTimeRange('all');
                  markManualOverride();
                }}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>

        {/* Board / Timeline */}
        {activeView === 'board' ? (
          <div className="flex flex-1 overflow-hidden min-h-[300px] min-w-0">
            <div className="flex-1 overflow-auto min-h-0 min-w-0 p-2 md:p-4 print:overflow-visible print:p-0">
              <div
              className="flex gap-4 items-start min-h-full print:min-h-0 print:grid print:grid-cols-3 print:gap-2 print:h-auto print:items-start print:px-4 print:py-2 flex-row"
              data-print-board
              data-print-direction="ltr"
            >
                {/* Project columns */}
                {boardProjects.map((project) => (
                  <ProjectColumn
                    key={project.id}
                    project={project}
                    techs={techsByProject.get(project.id) ?? []}
                    readinessTechs={readinessTechsByProject.get(project.id) ?? []}
                    allTechs={data.techs}
                    assignments={assignmentsByProject.get(project.id) ?? []}
                    canEdit={canEdit}
                    crewEnabled={crewEnabled}
                    projectsEnabled={projectsEnabled}
                    safetyEnabled={safetyEnabled}
                    inventoryEnabled={inventoryEnabled}
                    payrollEnabled={payrollEnabled}
                    showFinancials={showFinancials}
                    orgToday={orgToday}
                    currency={currency}
                    costsRevealed={costsRevealed}
                    onToggleCosts={handleToggleCosts}
                    onDrop={handleDrop}
                    selectedTechIds={selectedTechIds}
                    onSelectToggle={handleSelectToggle}
                    crewLists={crewLists}
                    onAssignCrewList={handleAssignCrewList}
                    droppedTechIds={droppedTechIds}
                    onDropAnimationEnd={handleDropAnimationEnd}
                    highlightExpiredCerts={glanceFilter === 'expired-certs' || personaDefaults.highlightExpiredCerts}
                    readOnlyReason={restrictionReason}
                    coLocatedCount={project.location_fk_id != null ? coLocatedMap.counts.get(project.location_fk_id) : undefined}
                    coLocatedProjects={project.location_fk_id != null ? coLocatedMap.projects.get(project.location_fk_id) : undefined}
                    coLocatedFilterActive={coLocatedFilterLocationId === project.location_fk_id}
                    onCoLocatedClick={project.location_fk_id != null && coLocatedMap.counts.has(project.location_fk_id) ? () => {
                      setCoLocatedFilterLocationId((prev) => {
                        const next = prev === project.location_fk_id! ? null : project.location_fk_id!;
                        if (next !== null) setGlanceFilter(null);
                        return next;
                      });
                    } : undefined}
                    terminology={terminology}
                    onTouchAssign={isTouchDevice && canEdit ? (techId, techName, assignmentId) => {
                      const personId = data.techs.find((t) => t.id === techId)?.person_id ?? techId;
                      setTouchAssignTarget({ techId, personId, techName, assignmentId });
                    } : undefined}
                    onWorkPackage={setWorkPackageProjectId}
                    onFindCrew={setFindCrewProjectId}
                    supervisionGap={canViewSupervision && supervisionGapProjectIds.has(project.id)}
                    onSupervisionGapClick={() => setSupervisionPanelOpen(true)}
                    jobColors={jobColors}
                    placementsByTech={techJobAssignments}
                    onBadgeClick={handleBadgeClick}
                  />
                ))}

                {boardProjects.length === 0 && (() => {
                  const isGenuinelyEmpty = data.projects.length === 0;
                  const headline = isGenuinelyEmpty
                    ? personaDefaults.emptyStateHeadline
                    : personaDefaults.filteredEmptyHeadline;
                  const description = isGenuinelyEmpty
                    ? personaDefaults.emptyStateDescription
                    : personaDefaults.filteredEmptyDescription;
                  return (
                    <div className="flex flex-col items-center justify-center flex-1 gap-1 py-12 text-center">
                      <p className="text-sm font-medium text-gray-500" data-testid="empty-state-headline">
                        {headline}
                      </p>
                      <p className="text-xs text-gray-400 max-w-xs">
                        {description}
                      </p>
                      {!isGenuinelyEmpty && (
                        <button
                          type="button"
                          className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
                          onClick={() => {
                            setProjectStatusFilter('');
                            setGlanceFilter(null);
                            setTimeRange('all');
                            setCoLocatedFilterLocationId(null);
                            markManualOverride();
                          }}
                          data-testid="empty-state-clear-filters"
                        >
                          Clear all filters
                        </button>
                      )}
                    </div>
                  );
                })()}

                {/* Unassigned techs (bench) — pinned to the right edge while the job area scrolls */}
                <div
                  ref={unassignedColumnRef}
                  className="flex flex-shrink-0 flex-col border-l border-gray-300 pl-4 sm:sticky sm:-right-2 sm:z-30 sm:-mr-2 sm:ml-auto sm:items-end sm:self-stretch sm:border-slate-300 sm:bg-slate-100 sm:pr-2 sm:shadow-[-8px_0_12px_-6px_rgba(15,23,42,0.15)] md:-right-4 md:-mr-4 md:pr-4 print:static print:z-auto print:mx-0 print:border-gray-400 print:bg-white print:px-2 print:shadow-none"
                  data-testid="unassigned-bench"
                >
                  <ProjectColumn
                    project={null}
                    techs={unassignedTechs}
                    assignments={[]}
                    canEdit={canEdit}
                    crewEnabled={crewEnabled}
                    projectsEnabled={projectsEnabled}
                    safetyEnabled={safetyEnabled}
                    inventoryEnabled={inventoryEnabled}
                    payrollEnabled={payrollEnabled}
                    showFinancials={showFinancials}
                    orgToday={orgToday}
                    currency={currency}
                    costsRevealed={costsRevealed}
                    onToggleCosts={handleToggleCosts}
                    onDrop={handleDrop}
                    selectedTechIds={selectedTechIds}
                    onSelectToggle={handleSelectToggle}
                    droppedTechIds={droppedTechIds}
                    onDropAnimationEnd={handleDropAnimationEnd}
                    highlightExpiredCerts={glanceFilter === 'expired-certs' || personaDefaults.highlightExpiredCerts}
                    readOnlyReason={restrictionReason}
                    terminology={terminology}
                    onTouchAssign={isTouchDevice && canEdit ? (techId, techName, assignmentId) => {
                      const personId = data.techs.find((t) => t.id === techId)?.person_id ?? techId;
                      setTouchAssignTarget({ techId, personId, techName, assignmentId });
                    } : undefined}
                    jobColors={jobColors}
                    placementsByTech={techJobAssignments}
                    onBadgeClick={handleBadgeClick}
                  />
                </div>

              </div>
            </div>

            {/* Sticky notes FAB — visible on small screens; side rail on desktop */}
            <button
              onClick={() => setStickyNotesPanelOpen(true)}
              className="fixed bottom-20 right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500 text-white shadow-lg active:scale-95 transition-transform print:hidden lg:hidden"
              aria-label="Open notes"
              data-testid="sticky-notes-fab"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8Z"/><path d="M15 3v4a2 2 0 0 0 2 2h4"/></svg>
            </button>
            <div className="hidden lg:flex w-64 lg:w-72 flex-shrink-0 overflow-y-auto min-h-0 min-w-0 border-l border-gray-200 bg-gray-50/50 p-3">
              <StickyNotesPanel
                canEdit={canEdit}
                readOnlyReason={restrictionReason}
                timeZone={timeZone}
              />
            </div>

            {/* Supervision cockpit rail */}
            {supervisionPanelOpen && (
              <SupervisionCockpitPanel
                onClose={() => setSupervisionPanelOpen(false)}
              />
            )}

            {/* Compliance evidence rail */}
            {evidencePanelOpen && (
              <ComplianceEvidencePanel
                projects={filteredProjects}
                techs={data.techs}
                assignments={data.assignments}
                safetyEnabled={safetyEnabled}
                inventoryEnabled={inventoryEnabled}
                crewEnabled={crewEnabled}
                onOpenDispatchHistory={() => setHistoryDrawerOpen(true)}
                onClose={() => setEvidencePanelOpen(false)}
              />
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-hidden">
            <TimelineView />
          </div>
        )}
      </div>

      <ConfirmAssignDialog
        key={pendingConfirm?.id ?? 'closed'}
        open={pendingConfirm !== null}
        warnings={pendingConfirm?.warnings}
        message={pendingConfirm?.message}
        title={pendingConfirm?.title}
        confirmLabel={pendingConfirm?.confirmLabel}
        pendingLabel={pendingConfirm?.pendingLabel}
        names={pendingConfirm?.names}
        canOverride={pendingConfirm?.canOverride}
        onConfirm={handleConfirm}
        onCancel={handleCancelConfirm}
        isPending={isMutating}
      />

      <DispatchDialog
        open={dispatchOpen}
        onClose={() => setDispatchOpen(false)}
        onDispatched={handleDispatched}
        onViewHistory={() => setHistoryDrawerOpen(true)}
        terminology={terminology}
        assignmentIds={undispatchedAssignmentIds}
      />

      <H3ightboardUnsavedChangesDialog {...unsavedChangesGuard} />

      <DispatchHistoryDrawer
        open={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        timeZone={timeZone}
      />

      <WorkPackagePanel
        open={workPackageProjectId !== null}
        onClose={() => setWorkPackageProjectId(null)}
        projectId={workPackageProjectId}
      />

      <FindCrewDialog
        open={findCrewProjectId !== null}
        project={findCrewProjectId != null ? data?.projects.find((project) => project.id === findCrewProjectId) ?? null : null}
        onClose={() => setFindCrewProjectId(null)}
        onAssign={(candidate) => {
          if (!findCrewProjectId) return;
          handleDrop({ techId: candidate.tech_id, personId: candidate.person_id }, findCrewProjectId);
          setFindCrewProjectId(null);
        }}
      />

      {/* Touch-safe project picker for assign/move */}
      {touchAssignTarget && (
        <TouchAssignPicker
          techName={touchAssignTarget.techName}
          techId={touchAssignTarget.techId}
          assignmentId={touchAssignTarget.assignmentId}
          projects={filteredProjects}
          onAssign={(techId, assignmentId, targetProjectId) => {
            handleDrop({ techId, personId: touchAssignTarget.personId, assignmentId }, targetProjectId);
          }}
          onClose={() => setTouchAssignTarget(null)}
        />
      )}

      <ShortcutOverlay
        open={shortcutOverlayOpen}
        onClose={() => setShortcutOverlayOpen(false)}
      />
      <TouchDragPreview />

      {/* Sticky notes bottom sheet — small screens */}
      {stickyNotesPanelOpen && (
        <div className="fixed inset-0 z-50 print:hidden lg:hidden" data-testid="sticky-notes-sheet">
          <div className="absolute inset-0 bg-black/40" onClick={() => setStickyNotesPanelOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[70vh] overflow-y-auto rounded-t-2xl bg-white p-4 shadow-xl animate-in slide-in-from-bottom duration-300" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)' }}>
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gray-300" />
            <StickyNotesPanel
              canEdit={canEdit}
              readOnlyReason={restrictionReason}
              timeZone={timeZone}
            />
          </div>
        </div>
      )}
    </DndProvider>
  );
}
